import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, AlertCircle, Store } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showShopifyConnect, setShowShopifyConnect] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.success) {
        localStorage.setItem("ff_authenticated", "true");
        localStorage.setItem("ff_user", res.data.user.email);
        localStorage.setItem("ff_access_token", res.data.access_token);
        onLogin(true);
      } else {
        setError(res.data.message || "Invalid email or password");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    }
    setLoading(false);
  };

  const handleShopifyConnect = (e) => {
    e.preventDefault();
    let shop = shopDomain.trim().toLowerCase();
    if (!shop) return;
    if (!shop.includes(".myshopify.com")) {
      shop = shop.replace(/\.myshopify\.com$/, "") + ".myshopify.com";
    }
    window.location.href = `${API}/shopify/install?shop=${encodeURIComponent(shop)}`;
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/easywaybill-icon.webp" alt="EasyWaybill" className="w-16 h-16 mx-auto mb-4 rounded-xl" />
          <h1 className="text-2xl font-bold text-slate-900">EasyWaybill</h1>
          <p className="text-slate-500 text-sm mt-1">Sign in to manage your waybills</p>
        </div>

        <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-slate-700 text-sm font-medium mb-1 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your-email@company.co.za"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-slate-700 text-sm font-medium mb-1 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                required
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-2 rounded-md">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </span>
              )}
            </button>
          </form>

          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
            <p className="text-slate-600 text-sm">
              Store connected but can't sign in?{" "}
              <a href="/auth" className="text-green-600 font-medium hover:text-green-700">Reset your password</a>
              {" "}— your store link and subscription stay intact.
            </p>
          </div>

          <div className="mt-6 text-center">
            <a href="/auth" className="text-sm text-slate-600">
              Don't have an account? <span className="text-green-600 font-medium hover:text-green-700">Create one</span>
            </a>
          </div>

          <div className="relative mt-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-slate-400">or</span>
            </div>
          </div>

          {!showShopifyConnect ? (
            <a
              href="https://apps.shopify.com/easywaybill"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 w-full bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-600 hover:to-green-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
              data-testid="shopify-connect-btn"
            >
              <Store className="w-4 h-4" />
              Sign Up with Shopify
            </a>
          ) : (
            <form onSubmit={handleShopifyConnect} className="mt-6 space-y-3">
              <div>
                <label className="text-slate-700 text-xs font-medium mb-1 block">Your Shopify store URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shopDomain}
                    onChange={(e) => setShopDomain(e.target.value)}
                    placeholder="your-store"
                    className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder:text-slate-400"
                    required
                    autoFocus
                    data-testid="shopify-domain-input"
                  />
                  <span className="flex items-center text-slate-400 text-sm">.myshopify.com</span>
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-600 hover:to-green-600 text-white py-2.5 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
                data-testid="shopify-connect-submit"
              >
                <Store className="w-4 h-4" />
                Connect Shopify Store
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-slate-400 text-xs mt-6">
          EasyWaybill &copy; {new Date().getFullYear()}
        </p>
        <div className="text-center mt-3">
          <span className="text-slate-400 text-xs">Developed by </span>
          <a href="https://supplywatch.app/agency" target="_blank" rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 text-xs font-medium">
            Starflight Agency <span className="text-[10px]">&nearr;</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
